import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useBlocker, useNavigate, useParams } from 'react-router-dom';
import {
    AlertTriangle, ArrowLeft, Check, Copy, Eye, GripVertical, Loader2,
    Plus, Save, Trash2, X,
} from 'lucide-react';
import { AxiosError } from 'axios';
import {
    DndContext, closestCenter,
    KeyboardSensor, PointerSensor,
    useSensor, useSensors,
    type DragEndEvent,
} from '@dnd-kit/core';
import {
    arrayMove, SortableContext,
    sortableKeyboardCoordinates, useSortable,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import {
    fetchImpactPreview,
    useCreateNoteType,
    useDuplicateNoteType,
    useNoteType,
    useNoteTypeUsage,
    useUpdateNoteType,
    type NoteType,
    type NoteTypeField,
    type NoteTypeFieldType,
    type NoteTypeImpactPreview,
    type NoteTypeSignature,
    type NoteTypeWritePayload,
} from '@/api/noteTypes';
import type { NoteField, NoteSignatureDef } from '@/api/notes';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { FormField, Input, Textarea } from '@/components/ui/FormField';
import { SelectField } from '@/features/clients/SelectField';
import {
    Dialog, DialogClose, DialogContent,
    DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/Dialog';
import { cn } from '@/lib/cn';

import { extractErrorMessage } from '@/lib/extractErrorMessage';
/*
 * Note Type builder — dedicated page at:
 *   /configuration/note-types/new      (create)
 *   /configuration/note-types/:id      (edit)
 *
 * Fixed layout the builder enforces:
 *   fields[]  → staff signatures  → client signature (pinned last, at most one)
 *
 * Save flow on EDIT: call /impact-preview against the candidate payload,
 * show the cascade dialog with per-bucket counts, and only PATCH after
 * user confirmation. The server is still authoritative — it runs the
 * same cascade on write. Preview is a UX aid, not a gate.
 *
 * Drag-reorder uses @dnd-kit. Fields and staff-signatures are each their
 * own SortableContext; the client signature is a fixed non-sortable slot
 * so it cannot be dragged out of the last position.
 */

type BuilderField = NoteTypeField;
type BuilderSig   = NoteTypeSignature;

const FIELD_TYPE_OPTIONS: Array<{ value: NoteTypeFieldType; label: string }> = [
    { value: 'text',        label: 'Short text' },
    { value: 'textarea',    label: 'Long text' },
    { value: 'number',      label: 'Number' },
    { value: 'date',        label: 'Date' },
    { value: 'checkbox',    label: 'Checkbox' },
    { value: 'select',      label: 'Dropdown (single)' },
    { value: 'multiselect', label: 'Dropdown (multi)' },
    { value: 'heading',     label: 'Section heading' },
];

const FULL_WIDTH_ELIGIBLE: NoteTypeFieldType[] = ['text', 'number', 'date', 'select'];
const DEFAULT_NAME = 'New note type';

function newFieldId(): string {
    return crypto.randomUUID();
}

// Sort object keys before serializing so two semantically equal payloads
// produce the same string regardless of key insertion order. PostgreSQL
// JSONB rewrites keys alphabetically on store, so the server response after
// save has different key order than what the builder created locally; a
// vanilla JSON.stringify comparison reports the form as dirty forever
// after the first save.
function stableStringify(value: unknown): string {
    return JSON.stringify(value, (_key, val) => {
        if (val && typeof val === 'object' && !Array.isArray(val)) {
            const sorted: Record<string, unknown> = {};
            for (const k of Object.keys(val).sort()) {
                sorted[k] = (val as Record<string, unknown>)[k];
            }
            return sorted;
        }
        return val;
    });
}

function makeField(type: NoteTypeFieldType): BuilderField {
    const base: BuilderField = { id: newFieldId(), label: '', type };
    if (type === 'select' || type === 'multiselect') base.choices = [];
    return base;
}

function makeStaffSig(): BuilderSig {
    return { id: newFieldId(), label: 'Staff signature', type: 'staff' };
}

function makeClientSig(): BuilderSig {
    return { id: newFieldId(), label: 'Parent signature', type: 'client' };
}

export function NoteTypeBuilderPage() {
    const { id } = useParams<{ id?: string }>();
    const isNew = !id || id === 'new';
    const navigate = useNavigate();

    const { data: existing, isLoading, isError, error } = useNoteType(isNew ? undefined : id);

    if (!isNew && isLoading) {
        return (
            <div className="p-6 max-w-[1200px] mx-auto">
                <div className="p-12 flex items-center justify-center text-text-subtle">
                    <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading…
                </div>
            </div>
        );
    }

    if (!isNew && (isError || !existing)) {
        return (
            <div className="p-6 max-w-[1200px] mx-auto">
                <BackLink />
                <Alert variant="danger">
                    Couldn't load this note type.{' '}
                    {extractErrorMessage(error, '')}
                </Alert>
            </div>
        );
    }

    return (
        <Editor
            initial={isNew ? null : existing!}
            onCreated={(newId) => navigate(`/configuration/note-types/${newId}`, { replace: true })}
        />
    );
}

function BackLink() {
    return (
        <Link
            to="/configuration?group=sessions&item=note-types"
            className="inline-flex items-center gap-1.5 text-sm text-text-muted hover:text-text mb-3"
        >
            <ArrowLeft className="w-3.5 h-3.5" /> Back to Configuration
        </Link>
    );
}

function Editor({
    initial, onCreated,
}: {
    initial: NoteType | null;
    onCreated: (id: string) => void;
}) {
    const isNew = !initial;
    const navigate = useNavigate();

    // ─── form state ──────────────────────────────────────────────────
    const [name, setName] = useState(initial?.name ?? DEFAULT_NAME);
    const [status, setStatus] = useState<'Active' | 'Inactive'>(initial?.status ?? 'Active');
    const [fields, setFields] = useState<BuilderField[]>(initial?.fields ?? []);
    const [staffSigs, setStaffSigs] = useState<BuilderSig[]>(
        (initial?.signatures ?? []).filter((s) => s.type === 'staff'),
    );
    const [clientSig, setClientSig] = useState<BuilderSig | null>(
        (initial?.signatures ?? []).find((s) => s.type === 'client') ?? null,
    );

    // Baseline copy so we can compute dirty / pass to impact preview. Held as
    // useState (not useRef) because mutating a ref doesn't trigger a re-render
    // — and the dirty useMemo needs to recompute after a successful save in
    // order to clear the unsaved indicator and disable the Save button.
    const [baseline, setBaseline] = useState({
        name:       initial?.name ?? DEFAULT_NAME,
        status:     initial?.status ?? ('Active' as 'Active' | 'Inactive'),
        fields:     initial?.fields ?? [] as NoteTypeField[],
        signatures: initial?.signatures ?? [] as NoteTypeSignature[],
    });

    const currentSignatures = useMemo<BuilderSig[]>(() => {
        return clientSig ? [...staffSigs, clientSig] : [...staffSigs];
    }, [staffSigs, clientSig]);

    const dirty = useMemo(() => {
        if (baseline.name !== name) return true;
        if (baseline.status !== status) return true;
        if (stableStringify(baseline.fields) !== stableStringify(fields)) return true;
        if (stableStringify(baseline.signatures) !== stableStringify(currentSignatures)) return true;
        return false;
    }, [baseline, name, status, fields, currentSignatures]);

    // ─── unsaved-changes guards ──────────────────────────────────────
    const blocker = useBlocker(
        useCallback(
            ({ currentLocation, nextLocation }: { currentLocation: { pathname: string }; nextLocation: { pathname: string } }) =>
                dirty && currentLocation.pathname !== nextLocation.pathname,
            [dirty],
        ),
    );

    useEffect(() => {
        if (!dirty) return;
        const handler = (e: BeforeUnloadEvent) => {
            e.preventDefault();
            e.returnValue = '';
        };
        window.addEventListener('beforeunload', handler);
        return () => window.removeEventListener('beforeunload', handler);
    }, [dirty]);

    // ─── mutations + impact preview ──────────────────────────────────
    const createM = useCreateNoteType();
    const updateM = useUpdateNoteType();
    const duplicateM = useDuplicateNoteType();

    const [preview, setPreview] = useState<NoteTypeImpactPreview | null>(null);
    const [impactLoading, setImpactLoading] = useState(false);
    const [saveErr, setSaveErr] = useState<string | null>(null);
    const [showPreview, setShowPreview] = useState(false);

    const { data: usage } = useNoteTypeUsage(initial?.id);

    function buildPayload(): NoteTypeWritePayload {
        return {
            name: name.trim() || DEFAULT_NAME,
            status,
            fields,
            signatures: currentSignatures,
        };
    }

    function clientValidate(): string | null {
        if (!name.trim()) return 'Name is required.';
        for (const f of fields) {
            if (f.type === 'heading') continue;
            if (!f.label.trim()) return 'Every field needs a label.';
            if ((f.type === 'select' || f.type === 'multiselect') && (!f.choices || f.choices.length === 0)) {
                return `Dropdown field "${f.label || 'unnamed'}" needs at least one choice.`;
            }
        }
        for (const s of staffSigs) {
            if (!s.label.trim()) return 'Every signature needs a label.';
        }
        if (clientSig && !clientSig.label.trim()) return 'The parent signature needs a label.';
        return null;
    }

    async function handleCreate() {
        setSaveErr(null);
        const err = clientValidate();
        if (err) { setSaveErr(err); return; }
        try {
            const created = await createM.mutateAsync(buildPayload());
            // Sync local form state to the server's representation so the
            // dirty check compares like-for-like (Postgres JSONB rewrites
            // object keys, so an in-place stableStringify on the new
            // baseline still has to match local key order).
            setName(created.name);
            setStatus(created.status);
            setFields(created.fields);
            setStaffSigs(created.signatures.filter((s) => s.type === 'staff'));
            setClientSig(created.signatures.find((s) => s.type === 'client') ?? null);
            setBaseline({
                name: created.name, status: created.status,
                fields: created.fields, signatures: created.signatures,
            });
            onCreated(created.id);
        } catch (e) {
            setSaveErr(extractErr(e));
        }
    }

    async function handleSaveExisting() {
        if (!initial) return;
        setSaveErr(null);
        const err = clientValidate();
        if (err) { setSaveErr(err); return; }
        setImpactLoading(true);
        try {
            const p = await fetchImpactPreview(initial.id, { fields, signatures: currentSignatures });
            setPreview(p);
        } catch (e) {
            setSaveErr(extractErr(e));
        } finally {
            setImpactLoading(false);
        }
    }

    async function confirmSave() {
        if (!initial) return;
        setSaveErr(null);
        try {
            const updated = await updateM.mutateAsync({ id: initial.id, payload: buildPayload() });
            // Sync local form state to the server's representation so the
            // dirty check compares like-for-like.
            setName(updated.name);
            setStatus(updated.status);
            setFields(updated.fields);
            setStaffSigs(updated.signatures.filter((s) => s.type === 'staff'));
            setClientSig(updated.signatures.find((s) => s.type === 'client') ?? null);
            setBaseline({
                name: updated.name, status: updated.status,
                fields: updated.fields, signatures: updated.signatures,
            });
            setPreview(null);
        } catch (e) {
            setSaveErr(extractErr(e));
        }
    }

    async function handleDuplicate() {
        if (!initial) return;
        try {
            const dup = await duplicateM.mutateAsync({ id: initial.id });
            navigate(`/configuration/note-types/${dup.id}`);
        } catch (e) {
            setSaveErr(extractErr(e));
        }
    }

    // ─── field helpers ────────────────────────────────────────────────
    function addField(type: NoteTypeFieldType) {
        setFields((prev) => [...prev, makeField(type)]);
    }
    function updateField(id: string, patch: Partial<BuilderField>) {
        setFields((prev) => prev.map((f) => (f.id === id ? { ...f, ...patch } : f)));
    }
    function deleteField(id: string) {
        setFields((prev) => prev.filter((f) => f.id !== id));
    }
    function onFieldDragEnd(e: DragEndEvent) {
        const { active, over } = e;
        if (!over || active.id === over.id) return;
        const oldIdx = fields.findIndex((f) => f.id === active.id);
        const newIdx = fields.findIndex((f) => f.id === over.id);
        if (oldIdx < 0 || newIdx < 0) return;
        setFields((prev) => arrayMove(prev, oldIdx, newIdx));
    }

    // ─── staff-sig helpers ────────────────────────────────────────────
    function addStaffSig() {
        setStaffSigs((prev) => [...prev, makeStaffSig()]);
    }
    function updateStaffSig(id: string, patch: Partial<BuilderSig>) {
        setStaffSigs((prev) => prev.map((s) => (s.id === id ? { ...s, ...patch } : s)));
    }
    function deleteStaffSig(id: string) {
        setStaffSigs((prev) => prev.filter((s) => s.id !== id));
    }
    function onStaffSigDragEnd(e: DragEndEvent) {
        const { active, over } = e;
        if (!over || active.id === over.id) return;
        const oldIdx = staffSigs.findIndex((s) => s.id === active.id);
        const newIdx = staffSigs.findIndex((s) => s.id === over.id);
        if (oldIdx < 0 || newIdx < 0) return;
        setStaffSigs((prev) => arrayMove(prev, oldIdx, newIdx));
    }

    // ─── client-sig helpers ───────────────────────────────────────────
    function addClientSig() {
        setClientSig(makeClientSig());
    }
    function updateClientSig(patch: Partial<BuilderSig>) {
        setClientSig((cur) => (cur ? { ...cur, ...patch } : cur));
    }
    function removeClientSig() {
        setClientSig(null);
    }

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
    );

    const savingBusy = createM.isPending || updateM.isPending || impactLoading;

    return (
        <div className="p-6 max-w-[1200px] mx-auto">
            <BackLink />

            {/* Header */}
            <div className="flex flex-wrap items-start justify-between gap-4 mb-5">
                <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 text-xs text-text-subtle mb-1">
                        <span>Configuration</span><span>·</span><span>Note Types</span>
                    </div>
                    <h1 className="font-display text-2xl font-bold text-text">
                        {isNew ? 'New note type' : (initial?.name || 'Note type')}
                    </h1>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <Button variant="ghost" size="sm" onClick={() => setShowPreview(true)}>
                        <Eye className="w-3.5 h-3.5" /> Preview
                    </Button>
                    {!isNew && (
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={handleDuplicate}
                            disabled={duplicateM.isPending}
                        >
                            <Copy className="w-3.5 h-3.5" /> Duplicate
                        </Button>
                    )}
                    <Button
                        variant="primary"
                        size="sm"
                        onClick={isNew ? handleCreate : handleSaveExisting}
                        disabled={savingBusy || !dirty && !isNew}
                    >
                        {savingBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                        {isNew ? 'Create' : 'Save'}
                    </Button>
                </div>
            </div>

            {saveErr && <Alert variant="danger" className="mb-4">{saveErr}</Alert>}

            <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-6">
                {/* Main builder column */}
                <div className="min-w-0 space-y-6">
                    {/* Basics */}
                    <Card className="p-4">
                        <h2 className="font-display text-sm font-bold text-text mb-3">Basics</h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4">
                            <FormField label="Name" required>
                                {(id) => (
                                    <Input
                                        id={id}
                                        value={name}
                                        onChange={(e) => setName(e.target.value)}
                                        placeholder="e.g. Direct Therapy Note"
                                    />
                                )}
                            </FormField>
                            <FormField label="Status">
                                {(id) => (
                                    <SelectField
                                        id={id}
                                        value={status}
                                        onChange={(e) => setStatus(e.target.value as 'Active' | 'Inactive')}
                                    >
                                        <option value="Active">Active</option>
                                        <option value="Inactive">Inactive</option>
                                    </SelectField>
                                )}
                            </FormField>
                        </div>
                    </Card>

                    {/* Fields */}
                    <Card className="p-4">
                        <div className="flex items-center justify-between mb-3">
                            <div>
                                <h2 className="font-display text-sm font-bold text-text">Fields</h2>
                                <p className="text-xs text-text-subtle">
                                    Drag to reorder. Section headings are display-only and never block Mark-Complete.
                                </p>
                            </div>
                            <AddFieldMenu onPick={addField} />
                        </div>

                        {fields.length === 0 ? (
                            <div className="text-center py-8 text-text-subtle text-sm">
                                No fields yet — add one above.
                            </div>
                        ) : (
                            <DndContext
                                sensors={sensors}
                                collisionDetection={closestCenter}
                                onDragEnd={onFieldDragEnd}
                            >
                                <SortableContext
                                    items={fields.map((f) => f.id)}
                                    strategy={verticalListSortingStrategy}
                                >
                                    <div className="space-y-2">
                                        {fields.map((f) => (
                                            <SortableFieldRow
                                                key={f.id}
                                                field={f}
                                                onChange={(patch) => updateField(f.id, patch)}
                                                onDelete={() => deleteField(f.id)}
                                            />
                                        ))}
                                    </div>
                                </SortableContext>
                            </DndContext>
                        )}
                    </Card>

                    {/* Staff signatures */}
                    <Card className="p-4">
                        <div className="flex items-center justify-between mb-3">
                            <div>
                                <h2 className="font-display text-sm font-bold text-text">Staff signatures</h2>
                                <p className="text-xs text-text-subtle">
                                    The staff picker and role field are built-in — you choose the label and (optionally) a helper line.
                                </p>
                            </div>
                            <Button variant="primary" size="sm" onClick={addStaffSig}>
                                <Plus className="w-3.5 h-3.5" /> Add signature
                            </Button>
                        </div>

                        {staffSigs.length === 0 ? (
                            <div className="text-center py-6 text-text-subtle text-sm">
                                No staff signatures yet.
                            </div>
                        ) : (
                            <DndContext
                                sensors={sensors}
                                collisionDetection={closestCenter}
                                onDragEnd={onStaffSigDragEnd}
                            >
                                <SortableContext
                                    items={staffSigs.map((s) => s.id)}
                                    strategy={verticalListSortingStrategy}
                                >
                                    <div className="space-y-2">
                                        {staffSigs.map((s) => (
                                            <SortableSigRow
                                                key={s.id}
                                                sig={s}
                                                onChange={(patch) => updateStaffSig(s.id, patch)}
                                                onDelete={() => deleteStaffSig(s.id)}
                                            />
                                        ))}
                                    </div>
                                </SortableContext>
                            </DndContext>
                        )}
                    </Card>

                    {/* Client signature (single, pinned last) */}
                    <Card className="p-4">
                        <div className="flex items-center justify-between mb-3">
                            <div>
                                <h2 className="font-display text-sm font-bold text-text">Parent signature</h2>
                                <p className="text-xs text-text-subtle">
                                    Optional. If present, it renders last and uses the practice-wide relationships list.
                                </p>
                            </div>
                            {clientSig ? (
                                <Button variant="ghost" size="sm" onClick={removeClientSig}>
                                    <Trash2 className="w-3.5 h-3.5" /> Remove
                                </Button>
                            ) : (
                                <Button variant="primary" size="sm" onClick={addClientSig}>
                                    <Plus className="w-3.5 h-3.5" /> Add parent signature
                                </Button>
                            )}
                        </div>

                        {clientSig && (
                            <ClientSigEditor sig={clientSig} onChange={updateClientSig} />
                        )}
                    </Card>
                </div>

                {/* Right column: Used-by */}
                <aside className="lg:sticky lg:top-4 self-start">
                    <Card className="p-4">
                        <h3 className="font-display text-sm font-bold text-text mb-2">Used by</h3>
                        {!initial ? (
                            <p className="text-xs text-text-subtle">Save the note type first to see where it's used.</p>
                        ) : !usage ? (
                            <p className="text-xs text-text-subtle">Loading…</p>
                        ) : usage.sessionTypes.length === 0 ? (
                            <p className="text-xs text-text-subtle">Not attached to any session type.</p>
                        ) : (
                            <ul className="text-sm space-y-1">
                                {usage.sessionTypes.map((st) => (
                                    <li key={st.id} className="flex items-center justify-between gap-2">
                                        <span className="truncate">{st.name}</span>
                                        <span className="text-2xs uppercase tracking-wider text-text-subtle">{st.status}</span>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </Card>
                </aside>
            </div>

            {/* Blocker dialog */}
            {blocker.state === 'blocked' && (
                <UnsavedDialog
                    onCancel={() => blocker.reset()}
                    onDiscard={() => blocker.proceed()}
                />
            )}

            {/* Preview dialog */}
            {showPreview && (
                <PreviewDialog
                    name={name || DEFAULT_NAME}
                    fields={fields}
                    signatures={currentSignatures}
                    onClose={() => setShowPreview(false)}
                />
            )}

            {/* Impact preview dialog (edit only) */}
            {preview && initial && (
                <ImpactDialog
                    preview={preview}
                    fields={fields}
                    signatures={currentSignatures}
                    prevFields={initial.fields}
                    prevSignatures={initial.signatures}
                    busy={updateM.isPending}
                    onCancel={() => setPreview(null)}
                    onConfirm={confirmSave}
                />
            )}
        </div>
    );
}

function extractErr(e: unknown): string {
    if (e instanceof AxiosError) {
        const data = e.response?.data;
        if (data && typeof data === 'object' && 'error' in data && typeof data.error === 'string') return data.error;
    }
    return extractErrorMessage(e, 'Could not save.');
}

// ═══ Add-field menu ══════════════════════════════════════════════════
function AddFieldMenu({ onPick }: { onPick: (t: NoteTypeFieldType) => void }) {
    const [open, setOpen] = useState(false);
    const wrapRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        if (!open) return;
        const handler = (e: MouseEvent) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
        };
        window.addEventListener('mousedown', handler);
        return () => window.removeEventListener('mousedown', handler);
    }, [open]);

    return (
        <div ref={wrapRef} className="relative">
            <Button variant="primary" size="sm" onClick={() => setOpen((o) => !o)}>
                <Plus className="w-3.5 h-3.5" /> Add field
            </Button>
            {open && (
                <div className="absolute right-0 top-full mt-1 z-10 w-48 bg-surface border border-border rounded-md shadow-lg py-1">
                    {FIELD_TYPE_OPTIONS.map((opt) => (
                        <button
                            key={opt.value}
                            type="button"
                            className="w-full text-left px-3 py-1.5 text-sm hover:bg-surface-muted"
                            onClick={() => { onPick(opt.value); setOpen(false); }}
                        >
                            {opt.label}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}

// ═══ Sortable field row ═════════════════════════════════════════════════
function SortableFieldRow({
    field, onChange, onDelete,
}: {
    field: BuilderField;
    onChange: (patch: Partial<BuilderField>) => void;
    onDelete: () => void;
}) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
        useSortable({ id: field.id });
    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };

    const isHeading = field.type === 'heading';
    const hasChoices = field.type === 'select' || field.type === 'multiselect';
    const canFullWidth = FULL_WIDTH_ELIGIBLE.includes(field.type);

    return (
        <div
            ref={setNodeRef}
            style={style}
            className="border border-border rounded-md p-3 bg-background"
        >
            <div className="flex items-start gap-2">
                <button
                    type="button"
                    className="text-text-subtle hover:text-text cursor-grab active:cursor-grabbing mt-1"
                    aria-label="Drag to reorder"
                    {...attributes}
                    {...listeners}
                >
                    <GripVertical className="w-4 h-4" />
                </button>
                <div className="flex-1 min-w-0 grid grid-cols-1 md:grid-cols-[1fr_180px] gap-x-3">
                    <FormField label={isHeading ? 'Heading text' : 'Label'} required>
                        {(id) => (
                            <Input
                                id={id}
                                value={field.label}
                                onChange={(e) => onChange({ label: e.target.value })}
                                placeholder={isHeading ? 'Section heading' : 'Field label'}
                            />
                        )}
                    </FormField>
                    <FormField label="Type">
                        {(id) => (
                            <SelectField
                                id={id}
                                value={field.type}
                                onChange={(e) => {
                                    const next = e.target.value as NoteTypeFieldType;
                                    // Swapping to/away from choice-bearing types — keep existing choices if present.
                                    const patch: Partial<BuilderField> = { type: next };
                                    if ((next === 'select' || next === 'multiselect') && !field.choices) {
                                        patch.choices = [];
                                    }
                                    // Clearing fullWidth if the target type doesn't support it.
                                    if (!FULL_WIDTH_ELIGIBLE.includes(next)) patch.fullWidth = false;
                                    onChange(patch);
                                }}
                            >
                                {FIELD_TYPE_OPTIONS.map((opt) => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                            </SelectField>
                        )}
                    </FormField>
                </div>
                <Button
                    variant="ghost"
                    size="xs"
                    aria-label="Delete field"
                    onClick={onDelete}
                >
                    <Trash2 className="w-3 h-3" />
                </Button>
            </div>

            <div className="pl-6 mt-1">
                <FormField label="Helper text">
                    {(id) => (
                        <Input
                            id={id}
                            value={field.helperText ?? ''}
                            onChange={(e) => onChange({ helperText: e.target.value || undefined })}
                            placeholder="Optional subline shown under the label"
                        />
                    )}
                </FormField>

                {!isHeading && (
                    <div className="flex flex-wrap items-center gap-4 -mt-1 mb-2">
                        <label className="inline-flex items-center gap-1.5 text-sm cursor-pointer">
                            <input
                                type="checkbox"
                                className="accent-accent"
                                checked={field.required !== false}
                                onChange={(e) => onChange({ required: e.target.checked ? undefined : false })}
                            />
                            <span>Required</span>
                        </label>
                        {canFullWidth && (
                            <label className="inline-flex items-center gap-1.5 text-sm cursor-pointer">
                                <input
                                    type="checkbox"
                                    className="accent-accent"
                                    checked={!!field.fullWidth}
                                    onChange={(e) => onChange({ fullWidth: e.target.checked || undefined })}
                                />
                                <span>Full width</span>
                            </label>
                        )}
                    </div>
                )}

                {hasChoices && (
                    <ChoicesEditor
                        choices={field.choices ?? []}
                        onChange={(next) => onChange({ choices: next })}
                    />
                )}
            </div>
        </div>
    );
}

// ═══ Choices editor (row-based) ══════════════════════════════════════════
function ChoicesEditor({
    choices, onChange,
}: {
    choices: string[];
    onChange: (next: string[]) => void;
}) {
    const [draft, setDraft] = useState('');

    function add() {
        const v = draft.trim();
        if (!v) return;
        if (choices.includes(v)) { setDraft(''); return; }
        onChange([...choices, v]);
        setDraft('');
    }

    function remove(i: number) {
        onChange(choices.filter((_, idx) => idx !== i));
    }

    function edit(i: number, v: string) {
        onChange(choices.map((c, idx) => (idx === i ? v : c)));
    }

    return (
        <fieldset className="border-t border-border pt-2 mt-1">
            <legend className="block text-xs font-bold text-text-muted uppercase tracking-wider mb-1.5">
                Choices
            </legend>
            <div className="space-y-1.5">
                {choices.map((c, i) => (
                    <div key={i} className="flex items-center gap-2">
                        <Input
                            value={c}
                            onChange={(e) => edit(i, e.target.value)}
                        />
                        <Button
                            variant="ghost"
                            size="xs"
                            aria-label="Remove choice"
                            onClick={() => remove(i)}
                        >
                            <X className="w-3 h-3" />
                        </Button>
                    </div>
                ))}
                <div className="flex items-center gap-2">
                    <Input
                        value={draft}
                        onChange={(e) => setDraft(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') { e.preventDefault(); add(); }
                        }}
                        placeholder="Add a choice"
                    />
                    <Button variant="ghost" size="xs" onClick={add} aria-label="Add choice">
                        <Plus className="w-3 h-3" />
                    </Button>
                </div>
            </div>
        </fieldset>
    );
}

// ═══ Sortable staff-sig row ══════════════════════════════════════════════
function SortableSigRow({
    sig, onChange, onDelete,
}: {
    sig: BuilderSig;
    onChange: (patch: Partial<BuilderSig>) => void;
    onDelete: () => void;
}) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
        useSortable({ id: sig.id });
    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };

    return (
        <div
            ref={setNodeRef}
            style={style}
            className="border border-border rounded-md p-3 bg-background"
        >
            <div className="flex items-start gap-2">
                <button
                    type="button"
                    className="text-text-subtle hover:text-text cursor-grab active:cursor-grabbing mt-1"
                    aria-label="Drag to reorder"
                    {...attributes}
                    {...listeners}
                >
                    <GripVertical className="w-4 h-4" />
                </button>
                <div className="flex-1 min-w-0 grid grid-cols-1 md:grid-cols-2 gap-x-3">
                    <FormField label="Label" required>
                        {(id) => (
                            <Input
                                id={id}
                                value={sig.label}
                                onChange={(e) => onChange({ label: e.target.value })}
                            />
                        )}
                    </FormField>
                    <FormField label="Helper text">
                        {(id) => (
                            <Input
                                id={id}
                                value={sig.helperText ?? ''}
                                onChange={(e) => onChange({ helperText: e.target.value || undefined })}
                                placeholder="Optional subline"
                            />
                        )}
                    </FormField>
                </div>
                <Button
                    variant="ghost"
                    size="xs"
                    aria-label="Delete signature"
                    onClick={onDelete}
                >
                    <Trash2 className="w-3 h-3" />
                </Button>
            </div>
        </div>
    );
}

// ═══ Client-sig editor (no reorder — pinned last) ══════════════════════
function ClientSigEditor({
    sig, onChange,
}: {
    sig: BuilderSig;
    onChange: (patch: Partial<BuilderSig>) => void;
}) {
    return (
        <div className="border border-border rounded-md p-3 bg-background">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-3">
                <FormField label="Label" required>
                    {(id) => (
                        <Input
                            id={id}
                            value={sig.label}
                            onChange={(e) => onChange({ label: e.target.value })}
                        />
                    )}
                </FormField>
                <FormField label="Helper text">
                    {(id) => (
                        <Input
                            id={id}
                            value={sig.helperText ?? ''}
                            onChange={(e) => onChange({ helperText: e.target.value || undefined })}
                            placeholder="Optional subline"
                        />
                    )}
                </FormField>
            </div>
        </div>
    );
}

// ═══ Unsaved-changes dialog ═════════════════════════════════════════════
function UnsavedDialog({
    onCancel, onDiscard,
}: {
    onCancel: () => void;
    onDiscard: () => void;
}) {
    return (
        <Dialog open onOpenChange={(o) => !o && onCancel()}>
            <DialogContent aria-describedby={undefined}>
                <DialogHeader>
                    <DialogTitle>Discard unsaved changes?</DialogTitle>
                </DialogHeader>
                <p className="text-sm text-text-muted">
                    You have edits that haven't been saved. Leave the page and throw them away?
                </p>
                <DialogFooter>
                    <Button variant="ghost" size="sm" onClick={onCancel}>Keep editing</Button>
                    <Button variant="danger" size="sm" onClick={onDiscard}>Discard</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

// ═══ Preview dialog ═════════════════════════════════════════════════════
// Exported for unit testing the interactive preview controls.
export function PreviewDialog({
    name, fields, signatures, onClose,
}: {
    name: string;
    fields: BuilderField[];
    signatures: BuilderSig[];
    onClose: () => void;
}) {
    // Map builder types → render types (they're a superset — compatible shapes).
    const renderFields: NoteField[] = fields.map((f) => ({
        id: f.id,
        label: f.label || '(unnamed)',
        type: f.type,
        required: f.required,
        helperText: f.helperText,
        fullWidth: f.fullWidth,
        choices: f.choices,
    }));
    const renderSigs: NoteSignatureDef[] = signatures.map((s) => ({
        id: s.id,
        label: s.label || '(unnamed)',
        type: s.type,
        helperText: s.helperText,
    }));

    return (
        <Dialog open onOpenChange={(o) => !o && onClose()}>
            <DialogContent
                aria-describedby={undefined}
                className="w-[min(calc(100vw-2rem),50rem)] max-h-[85vh]"
            >
                <DialogHeader>
                    <DialogTitle>Preview · {name}</DialogTitle>
                    <DialogDescription>
                        Try it out — type, check, and pick to see how staff will fill this note. Nothing here is saved.
                    </DialogDescription>
                </DialogHeader>

                <PreviewNote fields={renderFields} signatures={renderSigs} />

                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="ghost" size="sm">Close</Button>
                    </DialogClose>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function PreviewNote({
    fields, signatures,
}: {
    fields: NoteField[];
    signatures: NoteSignatureDef[];
}) {
    if (fields.length === 0 && signatures.length === 0) {
        return <Alert variant="info">No fields or signatures yet.</Alert>;
    }
    return (
        <div className="space-y-4">
            {fields.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4">
                    {fields.map((f) => <PreviewField key={f.id} field={f} />)}
                </div>
            )}
            {signatures.length > 0 && (
                <div className="space-y-3">
                    <h3 className="text-xs font-bold text-text-muted uppercase tracking-wider">Signatures</h3>
                    {signatures.map((s) => (
                        <div key={s.id} className="border border-border rounded-md p-3">
                            <div className="text-sm font-medium text-text">
                                {s.label}
                                {s.type === 'client' && (
                                    <span className="ml-2 text-2xs uppercase tracking-wider text-text-subtle">Parent</span>
                                )}
                            </div>
                            {s.helperText && <p className="text-xs text-text-subtle mt-0.5">{s.helperText}</p>}
                            <div className="mt-2 h-20 border border-dashed border-border rounded bg-surface-muted/50" />
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

// Interactive so the builder's Preview behaves like the real note form
// (see FieldRenderer in features/sessions/NoteForm.tsx) — staff can type,
// check, and pick to feel out the layout they're building. The state is
// throwaway: it lives only in this component and is discarded when the
// dialog closes. The preview never saves anywhere.
function PreviewField({ field }: { field: NoteField }) {
    const label = field.label || '(unnamed field)';
    const helper = field.helperText?.trim();
    const required = field.type !== 'heading' && field.required !== false;
    const spanClass = field.fullWidth ? 'md:col-span-2' : undefined;
    const choices = Array.isArray(field.choices) ? field.choices : [];

    // One value slot per shape: text covers text/number/date/select,
    // checked covers checkbox, selected covers multiselect. A given field
    // id is only ever one type within an open dialog.
    const [text, setText] = useState('');
    const [checked, setChecked] = useState(false);
    const [selected, setSelected] = useState<string[]>([]);

    if (field.type === 'heading') {
        return (
            <div className="md:col-span-2 mt-2 mb-1 pb-1 border-b border-border">
                <h3 className="font-display text-sm font-bold text-text">{label}</h3>
                {helper && <p className="mt-0.5 text-xs text-text-subtle">{helper}</p>}
            </div>
        );
    }

    if (field.type === 'checkbox') {
        return (
            <div className={cn('mb-3.5 flex items-start gap-2', spanClass)}>
                <input
                    type="checkbox"
                    id={field.id}
                    checked={checked}
                    onChange={(e) => setChecked(e.target.checked)}
                    className="mt-0.5 w-4 h-4 accent-accent cursor-pointer"
                />
                <label htmlFor={field.id} className="text-sm text-text select-none cursor-pointer">
                    <span>
                        {label}
                        {required && <span className="text-error ml-0.5">*</span>}
                    </span>
                    {helper && <span className="block text-xs text-text-subtle">{helper}</span>}
                </label>
            </div>
        );
    }

    if (field.type === 'textarea') {
        return (
            <FormField label={label} required={required} hint={helper} className="md:col-span-2">
                {(id) => (
                    <Textarea
                        id={id}
                        rows={3}
                        value={text}
                        onChange={(e) => setText(e.target.value)}
                    />
                )}
            </FormField>
        );
    }

    if (field.type === 'select') {
        return (
            <FormField label={label} required={required} hint={helper} className={spanClass}>
                {(id) => (
                    <SelectField id={id} value={text} onChange={(e) => setText(e.target.value)}>
                        <option value="">—</option>
                        {choices.map((c) => <option key={c} value={c}>{c}</option>)}
                    </SelectField>
                )}
            </FormField>
        );
    }

    if (field.type === 'multiselect') {
        return (
            <FormField label={label} required={required} hint={helper} className={spanClass}>
                {() => (
                    <div className="flex flex-col gap-1.5 py-1">
                        {choices.map((choice) => {
                            const isChecked = selected.includes(choice);
                            return (
                                <label key={choice} className="inline-flex items-center gap-1.5 text-sm cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={isChecked}
                                        onChange={(e) =>
                                            setSelected((prev) =>
                                                e.target.checked
                                                    ? [...prev, choice]
                                                    : prev.filter((c) => c !== choice),
                                            )
                                        }
                                        className="accent-accent"
                                    />
                                    {choice}
                                </label>
                            );
                        })}
                    </div>
                )}
            </FormField>
        );
    }

    const inputType = field.type === 'number' ? 'number' : field.type === 'date' ? 'date' : 'text';
    return (
        <FormField label={label} required={required} hint={helper} className={spanClass}>
            {(id) => (
                <Input
                    id={id}
                    type={inputType}
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                />
            )}
        </FormField>
    );
}

// ═══ Impact-preview dialog ══════════════════════════════════════════════
function ImpactDialog({
    preview, fields, signatures, prevFields, prevSignatures, busy, onCancel, onConfirm,
}: {
    preview: NoteTypeImpactPreview;
    fields: BuilderField[];
    signatures: BuilderSig[];
    prevFields: NoteTypeField[];
    prevSignatures: NoteTypeSignature[];
    busy: boolean;
    onCancel: () => void;
    onConfirm: () => void;
}) {
    const counts = preview.counts;
    const total = Object.values(counts).reduce((a, b) => a + b, 0);

    // Lookups so we can show human-readable labels for dropped / added entries.
    const prevFieldLabel = (id: string) => prevFields.find((f) => f.id === id)?.label ?? '(removed)';
    const prevSigLabel   = (id: string) => prevSignatures.find((s) => s.id === id)?.label ?? '(removed)';
    const curFieldLabel  = (id: string) => fields.find((f) => f.id === id)?.label ?? '(new)';
    const curSigLabel    = (id: string) => signatures.find((s) => s.id === id)?.label ?? '(new)';

    return (
        <Dialog open onOpenChange={(o) => !o && onCancel()}>
            <DialogContent
                aria-describedby={undefined}
                className="w-[min(calc(100vw-2rem),42rem)]"
            >
                <DialogHeader>
                    <DialogTitle>Review changes</DialogTitle>
                    <DialogDescription>
                        Finalized notes are never affected — they keep their own frozen snapshot.
                    </DialogDescription>
                </DialogHeader>

                {preview.reorderOnly ? (
                    <Alert variant="info">
                        This is a reorder-only change. No existing notes will shift status or lose data.
                    </Alert>
                ) : (
                    <div className="space-y-3">
                        {total === 0 && (
                            <Alert variant="info">No session notes currently reference this type.</Alert>
                        )}

                        <CountsGrid counts={counts} />

                        {preview.droppedFieldIds.length > 0 && (
                            <ChangeList
                                tone="danger"
                                title="Removed fields (values will be dropped)"
                                items={preview.droppedFieldIds.map(prevFieldLabel)}
                            />
                        )}
                        {preview.droppedSigIds.length > 0 && (
                            <ChangeList
                                tone="danger"
                                title="Removed signatures (signed data will be dropped)"
                                items={preview.droppedSigIds.map(prevSigLabel)}
                            />
                        )}
                        {preview.addedRequiredFieldIds.length > 0 && (
                            <ChangeList
                                tone="warning"
                                title="Newly required fields"
                                items={preview.addedRequiredFieldIds.map(curFieldLabel)}
                                hint="Complete or Submitted notes missing these will revert to Drafting."
                            />
                        )}
                        {preview.addedSigIds.length > 0 && (
                            <ChangeList
                                tone="warning"
                                title="Newly required signatures"
                                items={preview.addedSigIds.map(curSigLabel)}
                                hint="Complete or Submitted notes missing these will revert to Drafting."
                            />
                        )}
                        {preview.addedOptionalFieldIds.length > 0 && (
                            <ChangeList
                                tone="info"
                                title="New optional fields"
                                items={preview.addedOptionalFieldIds.map(curFieldLabel)}
                            />
                        )}
                    </div>
                )}

                <DialogFooter>
                    <Button variant="ghost" size="sm" onClick={onCancel} disabled={busy}>Cancel</Button>
                    <Button variant="primary" size="sm" onClick={onConfirm} disabled={busy}>
                        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                        Save changes
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function CountsGrid({ counts }: { counts: NoteTypeImpactPreview['counts'] }) {
    const rows: Array<{ label: string; value: number; tone?: 'warning' | 'danger' | 'info' | 'success' }> = [
        { label: 'Reverted to Drafting',    value: counts.revertedToDrafting, tone: 'warning' },
        { label: 'Submitted → Drafting',    value: counts.submittedRecalled,  tone: 'warning' },
        { label: 'Drafting → Complete',     value: counts.advancedToComplete, tone: 'success' },
        { label: 'Values pruned only',      value: counts.valuePruned,        tone: 'info' },
        { label: 'Unchanged',               value: counts.unchanged },
        { label: 'Finalized (unaffected)',  value: counts.finalizedUnaffected },
    ];
    return (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {rows.map((r) => (
                <div
                    key={r.label}
                    className={cn(
                        'border border-border rounded-md px-3 py-2 bg-background',
                        r.value > 0 && r.tone === 'warning' && 'border-[#f0d080] bg-[#fffbf0]',
                        r.value > 0 && r.tone === 'danger' && 'border-[rgb(192_57_43_/_0.25)] bg-[#fdf5f5]',
                    )}
                >
                    <div className="text-2xs uppercase tracking-wider text-text-subtle">{r.label}</div>
                    <div className="text-lg font-bold tabular-nums text-text">{r.value}</div>
                </div>
            ))}
        </div>
    );
}

function ChangeList({
    tone, title, items, hint,
}: {
    tone: 'warning' | 'danger' | 'info';
    title: string;
    items: string[];
    hint?: string;
}) {
    const Icon = tone === 'danger' ? Trash2 : tone === 'warning' ? AlertTriangle : Plus;
    return (
        <div>
            <div className={cn(
                'flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider mb-1',
                tone === 'danger' && 'text-error',
                tone === 'warning' && 'text-[#7a5a00]',
                tone === 'info' && 'text-text-muted',
            )}>
                <Icon className="w-3 h-3" /> {title}
            </div>
            <ul className="text-sm text-text list-disc pl-5">
                {items.map((label, i) => <li key={i}>{label}</li>)}
            </ul>
            {hint && <p className="text-xs text-text-subtle mt-1">{hint}</p>}
        </div>
    );
}

